import React, { useEffect, type ChangeEvent } from 'react';
import { FormattedMessage } from 'react-intl-next';
import Checkbox from '@atlaskit/checkbox';
import { FadeIn } from '@atlaskit/motion';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { type OpenInviteCheckboxProps } from './types';
import { messages } from '../i18n';
import { useAnalytics } from '../../analytics';
import { ViralSettingsAnalytics } from './ViralSettingsAnalytics';

export const OpenInviteCheckbox = (props: OpenInviteCheckboxProps): React.JSX.Element | null => {
	const { show, onChange, checkboxTheme = 'DEFAULT', isChecked } = props;
	const analytics = useAnalytics(ViralSettingsAnalytics);

	useEffect(() => {
		show &&
			analytics.userInvitesCheckboxRendered({
				container: checkboxTheme,
				value: isChecked,
			});
	}, [show, checkboxTheme, isChecked, analytics]);

	const onChangeWrapper = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.checked;
		analytics.userInvitesCheckboxClicked({
			value: value,
			container: checkboxTheme,
		});

		onChange(event);
	};

	if (!show) {
		return null;
	}

	return (
		<FadeIn entranceDirection="top">
			{(animationProps) => (
				<Box
					paddingBlockStart={checkboxTheme === 'MODAL' ? 'space.150' : 'space.050'}
					{...animationProps}
				>
					<Checkbox
						testId="testId-invite-people-viral-settings-open-invite"
						onChange={onChangeWrapper}
						isChecked={isChecked}
						label={
							<>
								<Text color="color.text">
									<FormattedMessage {...messages.openInviteDescription} />
								</Text>
								{checkboxTheme === 'MODAL' && (
									<Text color="color.text.subtlest" size="small">
										<FormattedMessage {...messages.openInviteExtendedDescription} />
									</Text>
								)}
							</>
						}
					/>
				</Box>
			)}
		</FadeIn>
	);
};

export default OpenInviteCheckbox;
